/* eslint-disable react/no-unescaped-entities, no-empty */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ExternalLink, LogOut, Save, Send, History, Eye, Settings as SettingsIcon, MessageSquare, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { formatINR, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const GOLD_PAIRS = [
  { label: "24K · 1 thulam", cashKey: "thulam_24k_cash", upiKey: "thulam_24k_upi", required: true },
  { label: "24K · 50 grams", cashKey: "g50_24k_cash", upiKey: "g50_24k_upi" },
  { label: "24K · 100 grams", cashKey: "g100_24k_cash", upiKey: "g100_24k_upi" },
  { label: "24K Coin · 0.100 gram", cashKey: "coin_100mg_24k_cash", upiKey: "coin_100mg_24k_upi" },
  { label: "24K Coin · 0.200 gram", cashKey: "coin_200mg_24k_cash", upiKey: "coin_200mg_24k_upi" },
];

const SILVER_PAIRS = [
  { label: "Silver 999 · 1 thulam", cashKey: "thulam_999_cash", upiKey: null, sameAsCash: true, required: true },
  { label: "Silver 999 · 500 grams", cashKey: "g500_999_cash", upiKey: "g500_999_upi" },
  { label: "Silver 999 · 1 kg", cashKey: "kg1_999_cash", upiKey: "kg1_999_upi" },
];

const SVBC_URL = "https://svbcgold.com/";

const emptyGold = Object.fromEntries(GOLD_PAIRS.flatMap((p) => [[p.cashKey, ""], ...(p.upiKey ? [[p.upiKey, ""]] : [])]));
const emptySilver = Object.fromEntries(SILVER_PAIRS.flatMap((p) => [[p.cashKey, ""], ...(p.upiKey ? [[p.upiKey, ""]] : [])]));

const toPayload = (gold, silver, note) => ({
  gold: Object.fromEntries(Object.entries(gold).map(([k, v]) => [k, v === "" || v === null ? null : Number(v)])),
  silver: Object.fromEntries(Object.entries(silver).map(([k, v]) => [k, v === "" || v === null ? null : Number(v)])),
  note: note || null,
});

const Cell = ({ v }) => v == null ? <span className="text-xs uppercase tracking-wider text-muted-foreground">Contact Store</span> : <span className="font-medium tabular-nums">{formatINR(v)}</span>;

const PreviewTables = ({ data }) => (
  <div className="grid md:grid-cols-2 gap-4">
    {[
      { title: "Gold", rows: data.gold_table },
      { title: "Silver", rows: data.silver_table },
    ].map((t) => (
      <div key={t.title} className="border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/50 font-serif">{t.title}</div>
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-left">Cash</th><th className="px-3 py-2 text-left">UPI</th></tr>
          </thead>
          <tbody>
            {t.rows.map((r, i) => (
              <tr key={i} className="border-t border-border/60">
                <td className="px-3 py-2">{r.item}</td>
                <td className="px-3 py-2"><Cell v={r.cash} /></td>
                <td className="px-3 py-2"><Cell v={r.upi} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const iframeRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [iframeBlocked, setIframeBlocked] = useState(false);

  const [gold, setGold] = useState({});
  const [silver, setSilver] = useState({});
  const [note, setNote] = useState("");
  const [latest, setLatest] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [settings, setSettings] = useState({ store_name: "", store_phone: "", store_whatsapp: "", store_address: "", store_maps_url: "", store_instagram: "" });
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    try {
      const [d, l, s] = await Promise.all([
        api.get("/rates/draft").catch(() => ({ data: {} })),
        api.get("/rates/latest").catch(() => ({ data: {} })),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      if (d.data?.gold_inputs) {
        setGold({ ...emptyGold, ...Object.fromEntries(Object.entries(d.data.gold_inputs).map(([k, v]) => [k, v ?? ""])) });
        setSilver({ ...emptySilver, ...Object.fromEntries(Object.entries(d.data.silver_inputs).map(([k, v]) => [k, v ?? ""])) });
        setNote(d.data.note || "");
      } else if (l.data?.published) {
        setGold({ ...emptyGold, ...Object.fromEntries(Object.entries(l.data.gold_inputs).map(([k, v]) => [k, v ?? ""])) });
        setSilver({ ...emptySilver, ...Object.fromEntries(Object.entries(l.data.silver_inputs).map(([k, v]) => [k, v ?? ""])) });
      }
      setLatest(l.data);
      setSettings(s.data || {});
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(() => setIframeKey(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const onIframeLoad = () => {
    const fr = iframeRef.current;
    try {
      if (fr && !fr.contentDocument && !fr.contentWindow?.length) setIframeBlocked(true);
    } catch {
      setIframeBlocked(true);
    }
  };

  const logout = () => { setToken(null); nav("/admin/login"); };

  const validate = () => {
    if (!gold.thulam_24k_cash || isNaN(Number(gold.thulam_24k_cash))) {
      toast.error("24K 1 thulam CASH is required.");
      return false;
    }
    if (!silver.thulam_999_cash || isNaN(Number(silver.thulam_999_cash))) {
      toast.error("Silver 999 1 thulam CASH is required.");
      return false;
    }
    return true;
  };

  const onSaveDraft = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/rates/draft", toPayload(gold, silver, note));
      toast.success("Draft saved");
    } catch (e) { toast.error("Could not save draft"); } finally { setLoading(false); }
  };

  const openPreview = async () => {
    if (!validate()) return;
    try {
      const { data } = await api.post("/rates/preview", toPayload(gold, silver, note));
      setPreview(data);
    } catch (e) { toast.error("Could not generate preview"); }
  };

  const onPublish = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/rates/publish", toPayload(gold, silver, note));
      toast.success("Today's rates published");
      const l = await api.get("/rates/latest");
      setLatest(l.data);
    } catch (e) { toast.error("Could not publish"); } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try { const { data } = await api.get("/rates/history"); setHistory(data); } catch {}
  };
  const loadEnquiries = async () => {
    try { const { data } = await api.get("/enquiries"); setEnquiries(data); } catch {}
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings updated");
    } catch { toast.error("Could not save settings"); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm gold-grad flex items-center justify-center text-white font-serif">S</div>
            <div>
              <div className="font-serif text-base">SSVJ Admin</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Rate Management</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={logout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />Logout
          </Button>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-2 gap-6">
        {/* LEFT: iframe */}
        <Card className="border-border shadow-none" data-testid="svbc-panel">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reference</p>
                <p className="font-serif text-lg">SVBC Gold Live Rates</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setIframeKey(Date.now())} data-testid="iframe-refresh">
                  <RefreshCw className="h-4 w-4 mr-2" />Refresh
                </Button>
                <Button asChild size="sm" className="rounded-sm bg-primary hover:bg-primary/90" data-testid="iframe-open-tab">
                  <a href={SVBC_URL} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open</a>
                </Button>
              </div>
            </div>
            <div className="relative bg-muted/30" style={{ height: 720 }}>
              {!iframeBlocked ? (
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={SVBC_URL}
                  title="SVBC Gold"
                  className="absolute inset-0 w-full h-full"
                  onLoad={onIframeLoad}
                  onError={() => setIframeBlocked(true)}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8" data-testid="iframe-fallback">
                  <p className="text-muted-foreground max-w-md">
                    SVBC reference could not be displayed here. Please open it in a new tab.
                  </p>
                  <Button asChild className="mt-4 rounded-sm bg-primary hover:bg-primary/90">
                    <a href={SVBC_URL} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open SVBC Gold Live Rates</a>
                  </Button>
                </div>
              )}
            </div>
            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">Auto-refreshes every 60 seconds.</div>
          </CardContent>
        </Card>

        {/* RIGHT: form */}
        <Card className="border-border shadow-none">
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-border bg-muted/50 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Today's Update</p>
                <p className="font-serif text-lg">Rate Inputs</p>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="last-updated-admin">
                Last published: {latest?.published ? formatDateTime(latest.published_at) : "—"}
              </p>
            </div>

            <Tabs defaultValue="gold" className="p-5">
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="gold" data-testid="tab-gold">Gold</TabsTrigger>
                <TabsTrigger value="silver" data-testid="tab-silver">Silver</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history" onClick={loadHistory}>History</TabsTrigger>
                <TabsTrigger value="enq" data-testid="tab-enquiries" onClick={loadEnquiries}>Enquiries</TabsTrigger>
              </TabsList>

              <TabsContent value="gold">
                <PairedInputs pairs={GOLD_PAIRS} values={gold} setValues={setGold} metal="gold" />
              </TabsContent>

              <TabsContent value="silver">
                <PairedInputs pairs={SILVER_PAIRS} values={silver} setValues={setSilver} metal="silver" />
              </TabsContent>

              <TabsContent value="history">
                <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="history-list">
                  {history.length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
                  {history.map((h) => (
                    <div key={h.id} className="border border-border rounded-sm p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{formatDateTime(h.published_at)}</span>
                        <span className="text-xs text-muted-foreground">by {h.published_by}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        24K thulam: {formatINR(h.gold_inputs.thulam_24k_cash)} · Silver 999 thulam: {formatINR(h.silver_inputs.thulam_999_cash)}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="enq">
                <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="enquiries-list">
                  {enquiries.length === 0 && <p className="text-sm text-muted-foreground">No enquiries yet.</p>}
                  {enquiries.map((e) => (
                    <div key={e.id} className={`border border-border rounded-sm p-3 text-sm ${e.read ? "opacity-60" : ""}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{e.name}</span> · <span className="text-muted-foreground">{e.phone}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDateTime(e.created_at)}</span>
                      </div>
                      {e.item && <div className="text-xs text-primary mt-1">{e.item}</div>}
                      {e.message && <div className="text-sm mt-1">{e.message}</div>}
                      <div className="flex gap-2 mt-2">
                        {!e.read && (
                          <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={async () => { await api.patch(`/enquiries/${e.id}/read`); loadEnquiries(); }} data-testid={`enq-read-${e.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />Mark read
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="rounded-sm h-7 text-destructive" onClick={async () => { await api.delete(`/enquiries/${e.id}`); loadEnquiries(); }} data-testid={`enq-del-${e.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <div className="border-t border-border p-5">
              <Label htmlFor="note" className="text-xs">Public note (optional)</Label>
              <Textarea id="note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm" placeholder="Eg: GST extra. Rates valid till 8pm." data-testid="input-note" />
            </div>

            <div className="border-t border-border p-5 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" className="rounded-sm" onClick={onSaveDraft} disabled={loading} data-testid="btn-save-draft">
                <Save className="h-4 w-4 mr-2" />Save Draft
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-sm" onClick={openPreview} data-testid="btn-preview">
                    <Eye className="h-4 w-4 mr-2" />Preview
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader><DialogTitle>Preview — Customer View</DialogTitle></DialogHeader>
                  {preview && <PreviewTables data={preview} />}
                </DialogContent>
              </Dialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="rounded-sm bg-primary hover:bg-primary/90" disabled={loading} data-testid="btn-publish">
                    <Send className="h-4 w-4 mr-2" />Publish Today's Rates
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Publish today's rates?</AlertDialogTitle>
                    <AlertDialogDescription>Customers will immediately see these rates on the public site.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="publish-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onPublish} data-testid="publish-confirm">Yes, publish</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings panel */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 pb-10">
        <Card className="border-border shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <SettingsIcon className="h-4 w-4 text-primary" />
              <h3 className="font-serif text-xl">Store Contact Settings</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Store Name</Label><Input value={settings.store_name || ""} onChange={(e) => setSettings({ ...settings, store_name: e.target.value })} data-testid="set-name" /></div>
              <div><Label>Phone (Call)</Label><Input value={settings.store_phone || ""} onChange={(e) => setSettings({ ...settings, store_phone: e.target.value })} data-testid="set-phone" placeholder="+91XXXXXXXXXX" /></div>
              <div><Label>WhatsApp Number</Label><Input value={settings.store_whatsapp || ""} onChange={(e) => setSettings({ ...settings, store_whatsapp: e.target.value })} data-testid="set-whatsapp" placeholder="+91XXXXXXXXXX" /></div>
              <div><Label>Maps URL</Label><Input value={settings.store_maps_url || ""} onChange={(e) => setSettings({ ...settings, store_maps_url: e.target.value })} data-testid="set-maps" /></div>
              <div><Label>Instagram URL</Label><Input value={settings.store_instagram || ""} onChange={(e) => setSettings({ ...settings, store_instagram: e.target.value })} data-testid="set-instagram" placeholder="https://www.instagram.com/..." /></div>
              <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={settings.store_address || ""} onChange={(e) => setSettings({ ...settings, store_address: e.target.value })} data-testid="set-address" /></div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveSettings} className="rounded-sm bg-primary hover:bg-primary/90" disabled={loading} data-testid="settings-save">
                <Save className="h-4 w-4 mr-2" />Save Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


const PairedInputs = ({ pairs, values, setValues, metal }) => {
  const setVal = (key, v) => setValues({ ...values, [key]: v });
  return (
    <div className="grid grid-cols-[1.4fr_1fr] gap-x-3 gap-y-2 items-end max-w-xl" data-testid={`paired-${metal}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground pb-1">Item</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-medium pb-1 flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />Rate (₹)
      </div>
      {pairs.map((p) => (
        <Row key={p.cashKey} pair={p} values={values} setVal={setVal} metal={metal} />
      ))}
    </div>
  );
};

const Row = ({ pair, values, setVal, metal }) => (
  <>
    <div className="text-sm text-foreground py-1.5">
      {pair.label}{pair.required && <span className="text-destructive ml-1">*</span>}
    </div>
    <Input
      type="number"
      inputMode="decimal"
      step="any"
      required={pair.required}
      value={values[pair.cashKey] ?? ""}
      onChange={(e) => setVal(pair.cashKey, e.target.value)}
      className="rounded-sm bg-amber-50/40 border-amber-200 focus-visible:ring-amber-500"
      data-testid={`input-${pair.cashKey}`}
      placeholder="0"
    />
  </>
);
